import { useQuery } from '@tanstack/react-query';
import { PkButton, PkEmptyMessage, PkIcon } from '@uefadigital/panenka-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import parseHtmlToReact from 'html-react-parser';
import { memo, useMemo } from 'react';

import { useVideoPlaylist } from '../../hooks/videos';
import { useVideoPlaylistOptions, useVideoPlaylistStore } from '../../store';

import { getVideoUrl } from '@services/LinkRuleService';
import { useTranslateFn } from '@shared/Translate';
import VideoPlayer from '@shared/VideoPlayer';
import { loadFromAPIs } from '@utilities/fetch';
import { parseUrl } from '@utilities/url';

import * as classes from './styles.module.scss';

dayjs.extend(relativeTime);

const CurrentVideo = () => {
  const t = useTranslateFn();
  const { options } = useVideoPlaylistOptions();
  const { videos } = useVideoPlaylist();
  const filtersActive = useVideoPlaylistStore((state) => Object.values(state.filters).some(Boolean));
  const video = useVideoPlaylistStore((state) => state.selectedVideo);
  const clear = useVideoPlaylistStore((state) => state.clearFilters);

  const { data: videoUrl } = useQuery({
    queryKey: ['video-url', video?.id],
    queryFn: ({ signal }) => loadFromAPIs<string>(getVideoUrl(video?.id, 'url/'), null, { signal }),
    select: (url) => parseUrl(url),
    enabled: !!video,
  });

  const handleResetFilter = () => {
    clear();
  };

  const timeAgo = useMemo(() => {
    if (!video) return null;
    const d = dayjs(video.publicationDate);
    if (dayjs().diff(d, 'day') > 14) {
      return d.format('DD/MM/YYYY');
    }

    return d.fromNow();
  }, [video]);

  return (
    <>
      {videoUrl ? (
        <div>
          <VideoPlayer
            key={video.id}
            teaser={video}
            isAutoPlay
            checkInteraction={false}
            minimizeOnScroll={options.sticky}
            minimizeOnScrollMobile={options.sticky}
            blockHighlights={options.blockHighlights}
            maxTimeAgo={options.maxTimeAgo}
            sharing
            sharingUrl={videoUrl}
            sharingTxt={video.title}
            showTitle={false}
          />
        </div>
      ) : (
        <div className='videoplayer-skl'>
          {!videos?.length && (
            <PkEmptyMessage className={classes.emptyMessage}>
              {t('videos_will_appear_here')}
              {filtersActive && (
                <PkButton slot='action' onPkClick={handleResetFilter} appearance='text'>
                  {t('stat_reset_filters')}
                  <PkIcon name='navigation-chevron-right' color='interaction' />
                </PkButton>
              )}
            </PkEmptyMessage>
          )}
        </div>
      )}
      <div className={classes.details}>
        <div className={classes.title}>{parseHtmlToReact(video?.headline ?? '')}</div>
        <div className={classes.date}>{timeAgo}</div>
        <div className={classes.description}>{parseHtmlToReact(video?.htmlDescription ?? '')}</div>
      </div>
    </>
  );
};

export default memo(CurrentVideo);
